import { gql } from 'graphql-request';
import { client } from '@/graph/client';
import { listUsers } from './types/listUsers';
import { KeyValuePair } from '@/graph/types/global';
import { listPermissions } from './types/permissionsList';
import { createUser } from './types/createUser';
import { updateUser } from './types/updateUser';
import { deleteUser } from './types/deleteUser';

/**

{
	scFindUsers
	{
		users {
			id
			name
			key
			secret
			salesOrganisation
		}
	}
}

{
	scFindUsers(key:"test")
	{
		users {
			id
			name
			key
			secret
			salesOrganisation
			permission {
				key
				value
				type
			}
			settings {
				key
				value
				type
			}
		}
	}
}

 */

export type UsersList = {
	id: string,
	name: string,
	key: string,
	secret: string,
	salesOrganisation: string[],
}

export type User = UsersList & {
	permission?: KeyValuePair[]
    settings?: KeyValuePair[]
}

class Users
{
	public async list(): Promise<UsersList[]>
	{
		const out:UsersList[] = [];

		const query = gql`
			{
				scFindUsers
				{
					users {
						id
						name
						key
						secret
						salesOrganisation
					}
				}
			}
		`;

		const response = await client.request<listUsers>(query);

		console.log("Users.list", response);

		if(response.scFindUsers.users)
		{
			for(const user of response.scFindUsers.users)
			{
				if(user.id && user.name)
				{
					out.push({
						id: user.id,
						name: user.name,
						key: user.key ?? "",
						secret: user.secret ?? "",
						salesOrganisation: user.salesOrganisation ?? [],
					});
				}
			}
		}

		return out;
	}

	public async get(userId: string): Promise<User[]>
	{
		const out:User[] = [];

		const query = gql`
			{
				scFindUsers(id:"` + userId + `")
				{
					users {
						id
						name
						key
						secret
						salesOrganisation
						permission {
							key
							value
							type
						}
						settings {
							key
							value
							type
						}
					}
				}
			}
		`;

		const response = await client.request<listUsers>(query);

		console.log("Users.get", response);

		if(response.scFindUsers.users)
		{
			for(const user of response.scFindUsers.users)
			{
				if(user.id && user.name)
				{
					out.push({
						id: user.id,
						name: user.name,
						key: user.key ?? "",
						secret: user.secret ?? "",
						salesOrganisation: user.salesOrganisation ?? [],
						permission: user.permission ?? [],
						settings: user.settings ?? [],
					});
				}
			}
		}

		return out;
	}

	public async createUser(name: string, key: string, secret: string): Promise<{ success: string; id: string; message?: string }>
	{
		const query = gql`
			mutation createUser($name: String!, $key: String!, $secret: String!) {
				scCreateUser(
					name: $name
					key: $key
					secret: $secret
				)
				{
					id
				}
			}
		`;

		const response = await client.request<createUser>(query, { name, key, secret });

		console.log("Users.createUser", response);

		return {
			id: response.scCreateUser.id,
			success: response.scCreateUser.success,
			message: response.scCreateUser.message ?? '',
		};
	}

	public async updateUser(id: string, name: string, key: string, secret: string, permission: KeyValuePair[], settings: KeyValuePair[], salesOrganisations: string[]): Promise<{ id: string, success: boolean }>
	{
		const query = gql`
			mutation scUpdateUser(
				$id: String!
				$name: String
				$key: String
				$secret: String
				$permission: [KeyValue]
				$settings: [KeyValue]
				$salesOrganisations: [String]
			) {
				scUpdateUser(
					id: $id
					name: $name
					key: $key
					secret: $secret
					permission: $permission
					settings: $settings
					salesOrganisations: $salesOrganisations
				) {
					success
					id
				}
			}
		`;

		const response = await client.request<updateUser>(query, { id, name, key, secret, permission, settings, salesOrganisations });

		console.log("Users.updateUser", response);

		return {
			id: response.scUpdateUser.id,
			success: response.scUpdateUser.success,
		};
	}

	public async deleteUser(id: string): Promise<{ id: string, success: boolean }>
	{
		const query = gql`
			mutation scDeleteUser(
				$id: String!
			) {
				scDeleteUser(
					id: $id
				) {
					success
					id
				}
			}
		`;

		const response = await client.request<deleteUser>(query, { id });

		console.log("Users.deleteUser", response);

		return {
			id: response.scDeleteUser.id,
			success: response.scDeleteUser.success,
		};
	}

	public async getAccessPermissions(): Promise<string[]>
	{
		const out: string[] = [];
		const query = gql`
			{
				scAccessList
				{
					name
				}
			}
		`;

		const response = await client.request<listPermissions>(query);

		console.log("Users.getAccessPermissions", response);

		if(response.scAccessList)
		{
			for(const perm of response.scAccessList)
			{
				out.push(perm.name);
			}
		}

		return out;
	}
}

export default new Users();